<template>
  <!--
    Editor-agnostic rich-text wrapper. The single job of this component is
    to keep the consuming sites (StoreBranding welcome message, CategoryCard
    category text) decoupled from the concrete editor implementation: its
    PUBLIC API (props/emits below) is deliberately editor-neutral so the
    editor swap is a ONE-file change confined to THIS component's internals
    -- nothing editor-specific leaks upward.

    Phase 2: TipTap (@tiptap/vue-3 + StarterKit + Link) under the hood.
    Same public API, same emitted-HTML tag set as the Phase 1 CKEditor
    build (p, br, strong/b, em/i, u, s, a, ul/ol/li, blockquote, h2/h3/h4)
    -- see sanitizeHtml.js allowlist. Headings are deliberately capped at
    H2/H3/H4 (StarterKit `heading.levels: [2, 3, 4]`) because the render
    sink strips <h1>.
  -->
  <div
    class="rte"
    :class="{ 'rte--readonly': readOnly }"
    :data-testid="dataTestid"
  >
    <div
      v-if="editor && !readOnly"
      class="rte__toolbar"
      data-testid="rte-toolbar"
    >
      <!-- Block-format dropdown: present on EVERY rich-text control (all
           our editors expose the paragraph-style menu). Offers Paragraph /
           Heading 2 / Heading 3 / Heading 4 -- NOT H1: an <h1> is stripped
           by the render-sink sanitizer (sanitizeMessageHtml allows h2,h3,h4
           only). -->
      <select
        v-if="!basic"
        class="rte__block-select"
        data-testid="rte-block-format"
        title="Block format"
        :value="currentBlockFormat"
        @change="onBlockFormatChange($event.target.value)"
      >
        <option value="paragraph">Paragraph</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="h4">Heading 4</option>
      </select>

      <!-- Font-family dropdown. Matches the block-format select pattern
           above. Applies the family as a bounded CSS CLASS mark
           (`<span class="rte-font-*">`) -- NOT inline font-family CSS, which
           both sanitizers strip by design. The families are self-hosted
           woff2 (no CDN / no CORS) declared in the global design-system
           stylesheet, so the same class renders for SHOPPERS on the public
           storefront, not just here in the editor. -->
      <select
        v-if="!basic"
        class="rte__font-select"
        data-testid="rte-font-family"
        title="Font"
        :value="currentFontFamily"
        @change="onFontFamilyChange($event.target.value)"
      >
        <option value="">Default font</option>
        <option
          v-for="font in fontOptions"
          :key="font.value"
          :value="font.value"
        >
          {{ font.label }}
        </option>
      </select>

      <span
        v-if="!basic"
        class="rte__sep"
        aria-hidden="true"
      ></span>

      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': editor.isActive('bold') }"
        data-testid="rte-btn-bold"
        title="Bold"
        @click="chain().toggleBold().run()"
      >
        <span class="material-icons">format_bold</span>
      </button>
      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': editor.isActive('italic') }"
        data-testid="rte-btn-italic"
        title="Italic"
        @click="chain().toggleItalic().run()"
      >
        <span class="material-icons">format_italic</span>
      </button>

      <span class="rte__sep" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        data-testid="rte-btn-bullet-list"
        title="Bulleted list"
        @click="chain().toggleBulletList().run()"
      >
        <span class="material-icons">format_list_bulleted</span>
      </button>
      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        data-testid="rte-btn-ordered-list"
        title="Numbered list"
        @click="chain().toggleOrderedList().run()"
      >
        <span class="material-icons">format_list_numbered</span>
      </button>

      <template v-if="!basic">
      <span class="rte__sep" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': editor.isActive('blockquote') }"
        data-testid="rte-btn-blockquote"
        title="Blockquote"
        @click="chain().toggleBlockquote().run()"
      >
        <span class="material-icons">format_quote</span>
      </button>

      <span class="rte__sep" aria-hidden="true"></span>

      <!-- Text alignment (left / center / right). TipTap's TextAlign
           extension writes style="text-align: <value>" on the block; the
           value is persisted through both sanitizers via a value-constrained
           style allowlist. No justify (Jake: left/center/right only). -->
      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': isAlignActive('left') }"
        data-testid="rte-btn-align-left"
        title="Align left"
        @click="chain().setTextAlign('left').run()"
      >
        <span class="material-icons">format_align_left</span>
      </button>
      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': isAlignActive('center') }"
        data-testid="rte-btn-align-center"
        title="Align center"
        @click="chain().setTextAlign('center').run()"
      >
        <span class="material-icons">format_align_center</span>
      </button>
      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': isAlignActive('right') }"
        data-testid="rte-btn-align-right"
        title="Align right"
        @click="chain().setTextAlign('right').run()"
      >
        <span class="material-icons">format_align_right</span>
      </button>

      </template>
      <span class="rte__sep" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tbtn"
        :class="{ 'is-active': editor.isActive('link') }"
        data-testid="rte-btn-link"
        title="Add / edit link"
        @click="onSetLink"
      >
        <span class="material-icons">link</span>
      </button>
      <button
        type="button"
        class="rte__tbtn"
        data-testid="rte-btn-unlink"
        title="Remove link"
        :disabled="!editor.isActive('link')"
        @click="chain().unsetLink().run()"
      >
        <span class="material-icons">link_off</span>
      </button>
      <button
        v-if="basic"
        type="button"
        class="rte__tbtn"
        data-testid="rte-btn-hard-break"
        title="Line break"
        @click="chain().setHardBreak().run()"
      >
        <span class="material-icons">keyboard_return</span>
      </button>

      <span class="rte__sep" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tbtn"
        data-testid="rte-btn-undo"
        title="Undo"
        :disabled="!editor.can().undo()"
        @click="chain().undo().run()"
      >
        <span class="material-icons">undo</span>
      </button>
      <button
        type="button"
        class="rte__tbtn"
        data-testid="rte-btn-redo"
        title="Redo"
        :disabled="!editor.can().redo()"
        @click="chain().redo().run()"
      >
        <span class="material-icons">redo</span>
      </button>

      <!-- Inline-image insert. Present ONLY when imagesEnabled (default
           false) so live sites (StoreBranding welcome message, CategoryCard)
           do not expose image authoring yet. Uploads to /api/upload_image
           and inserts an <img> whose src is host-restricted at the render
           sanitizer. -->
      <span
        v-if="imagesEnabled"
        class="rte__sep"
        aria-hidden="true"
      ></span>
      <button
        v-if="imagesEnabled"
        type="button"
        class="rte__tbtn"
        data-testid="rte-btn-image"
        title="Insert image (uploads to /api/upload_image)"
        :disabled="uploading"
        @click="openImagePicker"
      >
        <span
          v-if="uploading"
          class="material-icons rte__spin"
        >hourglass_top</span>
        <span v-else class="material-icons">image</span>
      </button>
    </div>

    <!-- Hidden native file input the Image button drives. Only rendered when
         imagesEnabled. Accepts web image types only. -->
    <input
      v-if="imagesEnabled"
      ref="imageInput"
      type="file"
      class="rte__file"
      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
      data-testid="rte-image-input"
      @change="onImageInputChange"
    />

    <p
      v-if="imagesEnabled && uploadError"
      class="rte__error"
      data-testid="rte-upload-error"
    >
      {{ uploadError }}
    </p>

    <!-- The contenteditable surface TipTap owns. -->
    <editor-content
      v-if="editor"
      :editor="editor"
      class="rte__surface"
      data-testid="rte-surface"
    />

    <p
      v-if="maxChars !== null && editor"
      class="rte__count"
      :class="{ 'rte__count--over': overLimit }"
      data-testid="rte-char-count"
    >{{ charCount }} / {{ maxChars }}</p>

    <!-- Link editor dialog. Replaces window.prompt: a URL input, an
         "open in new tab" checkbox (unchecked by default), and Save/Cancel.
         Opened by the toolbar link button or Mod-K; prefills the current
         link's href when editing, or a clipboard URL when adding new. -->
    <div
      v-if="linkDialog.open"
      class="rte__dialog-overlay"
      data-testid="rte-link-dialog"
      @click.self="closeLinkDialog"
      @keydown.esc.stop.prevent="closeLinkDialog"
    >
      <div
        class="rte__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rte-link-dialog-title"
      >
        <h2 id="rte-link-dialog-title" class="rte__dialog-title">
          {{ linkDialog.editing ? 'Edit link' : 'Add link' }}
        </h2>
        <form @submit.prevent="saveLinkDialog">
          <label class="rte__dialog-label" for="rte-link-url">
            Link URL
          </label>
          <input
            id="rte-link-url"
            ref="linkUrlInput"
            v-model="linkDialog.url"
            type="text"
            class="rte__dialog-input"
            data-testid="rte-link-url-input"
            placeholder="https://example.com"
            @keydown.esc.stop.prevent="closeLinkDialog"
          />
          <p
            v-if="basic"
            class="rte__dialog-hint"
            data-testid="rte-link-hint"
          >
            Only https:// and mailto: links are kept. Other links are removed
            when you save, leaving the text.
          </p>
          <label v-if="!basic" class="rte__dialog-checkbox">
            <input
              v-model="linkDialog.newTab"
              type="checkbox"
              data-testid="rte-link-newtab-checkbox"
            />
            <span>Open in new tab/window</span>
          </label>
          <div class="rte__dialog-actions">
            <button
              type="button"
              class="rte__dialog-btn"
              data-testid="rte-link-cancel"
              @click="closeLinkDialog"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="rte__dialog-btn rte__dialog-btn--primary"
              data-testid="rte-link-save"
            >
              {{ linkDialog.editing ? 'Save' : 'Add' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { Editor, EditorContent } from "@tiptap/vue-3";
import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";

import api from "@/services/api";
import { getImageBaseUrl } from "@/utils/imageBaseUrl";
import {
  ARTWORK_UPLOAD_ERROR_EMPTY,
  isEmptyFile,
} from "@/utils/artworkUploadContract";
import { planDescriptionLength } from "@/utils/sanitizePlanDescription";
import { useEnableInlineImages } from "@/composables/useEnableInlineImages";
import {
  RTE_FONTS,
  RTE_FONT_CLASS_PREFIX,
  RTE_FONT_CLASSES,
} from "@/utils/rteFonts";

// rel applied to new-tab links: prevents the opened page from reaching back
// through window.opener (noopener/noreferrer) and marks the link nofollow.
const NEW_TAB_REL = "noopener noreferrer nofollow";
// rel for same-tab links: no need for noopener (no new window), keep nofollow.
const SAME_TAB_REL = "nofollow";
// Clipboard values we will best-effort prefill into a NEW link's URL field.
const CLIPBOARD_URL_RE = /^(https?:\/\/|mailto:)/i;

// Web image types the inline-image insert accepts. A tighter, image-only
// subset of the shared artwork contract (which also allows PDFs / embroidery
// files that are not <img>-renderable). Mirrors the prototype.
const IMAGE_MIME_ALLOWLIST = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];
// Size cap (10 MB) -- generous for a web image, well under the backend's
// 50 MB artwork cap.
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;

// Heading levels the editor may emit. Deliberately H2/H3/H4 only -- the
// render-sink sanitizer (sanitizeMessageHtml, ALLOWED_TAGS) permits
// h2,h3,h4 and STRIPS <h1>, so producing an <h1> here would silently
// vanish at render. Capping StarterKit's heading levels makes an <h1>
// impossible to author in the first place (matches the CKEditor build,
// whose heading plugin emitted h2/h3/h4).
const HEADING_LEVELS = [2, 3, 4];

// Text-align values the editor may emit. Deliberately left/center/right only
// (Jake excluded `justify`) -- and matched by the value-constrained style
// allowlist in BOTH sanitizers (sanitizeHtml.js render sink + the backend
// rich_text_sanitizer write path). Applied to the heading + paragraph block
// types (TextAlign targets the block, writing style="text-align: <value>").
const TEXT_ALIGN_VALUES = ["left", "center", "right"];
const TEXT_ALIGN_TYPES = ["heading", "paragraph"];

// -- font family mark -------------------------------------------------------
//
// The font picker applies a family as a CSS CLASS on an inline <span>:
// `<span class="rte-font-lora">`. It is deliberately NOT an inline
// `style="font-family: ..."`, because BOTH sanitizers keep `style`
// value-constrained to `text-align` as CSS-injection defense -- a font-family
// declaration is dropped by design, so a style-based picker would look right
// in the editor and be silently stripped for shoppers. The families
// themselves are self-hosted woff2 declared in the GLOBAL design-system
// stylesheet (colors_and_type.css, imported once by main.js), so the same
// class renders identically on the public storefront and in this editor.
//
// The mark's parseHTML is allowlist-bound to RTE_FONT_CLASSES: hostile or
// stale markup carrying some other class does NOT become a mark and is not
// re-emitted. That is defense in depth -- the sanitizers are the real
// boundary, but the editor must not launder an arbitrary class either.
const FontFamily = Mark.create({
  name: "fontFamily",

  addAttributes() {
    return {
      // The class-name SUFFIX (e.g. "lora"); the rendered class is
      // `${RTE_FONT_CLASS_PREFIX}${font}`.
      font: {
        default: null,
        parseHTML: (element) => {
          // Take the FIRST approved font class present; ignore everything
          // else on the element.
          const tokens = (element.getAttribute("class") || "").split(/\s+/);
          const hit = tokens.find((t) => RTE_FONT_CLASSES.has(t));
          return hit ? hit.slice(RTE_FONT_CLASS_PREFIX.length) : null;
        },
        // Rendered by renderHTML below, not as a bare attribute.
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span",
        // Only adopt spans that actually carry an APPROVED font class.
        getAttrs: (element) => {
          const tokens = (element.getAttribute("class") || "").split(/\s+/);
          return tokens.some((t) => RTE_FONT_CLASSES.has(t)) ? {} : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes, mark }) {
    const font = mark.attrs.font;
    // A mark with no (or an unapproved) font renders as a bare span rather
    // than emitting a class we would not allow back in.
    const cls = RTE_FONT_CLASSES.has(`${RTE_FONT_CLASS_PREFIX}${font}`)
      ? { class: `${RTE_FONT_CLASS_PREFIX}${font}` }
      : {};
    return ["span", mergeAttributes(HTMLAttributes, cls), 0];
  },

  addCommands() {
    return {
      setFontFamily:
        (font) =>
        ({ chain }) => {
          if (!RTE_FONT_CLASSES.has(`${RTE_FONT_CLASS_PREFIX}${font}`)) {
            return false;
          }
          return chain().setMark(this.name, { font }).run();
        },
      unsetFontFamily:
        () =>
        ({ chain }) =>
          chain().unsetMark(this.name).run(),
    };
  },
});

export default {
  name: "RichTextEditor",

  components: { EditorContent },

  // The wrapper owns exactly the props it forwards; a stray `data-testid`
  // must land on the wrapper ROOT (a stable element callers address), not
  // fall through onto a child -- hence inheritAttrs: false + an explicit
  // `dataTestid` prop bound to the root <div>.
  inheritAttrs: false,

  props: {
    // v-model: the HTML string the editor edits. HTML in, HTML out -- the
    // content model stays editor-agnostic (see the sanitizer stack).
    modelValue: { type: String, default: "" },
    // Non-editable state (bound to a site's edit-mode / completion gate).
    readOnly: { type: Boolean, default: false },
    // Pass-through test id forwarded to the wrapper root so callers keep
    // addressing the editor by a stable data-testid.
    dataTestid: { type: String, default: null },
    // Inline-image authoring OVERRIDE. Normally the wrapper self-gates images
    // off the SuperAdmin `enable_inline_images` platform setting (read via
    // useEnableInlineImages below), so consuming sites need not pass anything.
    // A non-null value here FORCES the gate on/off, bypassing the SA setting
    // -- for tests and any edge case that needs an explicit value. null =
    // "defer to the SA setting" (the normal path). See the `imagesEnabled`
    // computed. The render-side sanitizer host-restricts any <img> regardless.
    imagesEnabledOverride: { type: Boolean, default: null },
    // Toolbar + schema variant. "full" (default) is the storefront editor.
    // "basic" is a restrained editor (bold, italic, lists, link, line break)
    // whose SCHEMA also lacks headings, blockquote, images, fonts and
    // alignment, so content that cannot be authored cannot be pasted either.
    toolbar: {
      type: String,
      default: "full",
      validator: (v) => v === "full" || v === "basic",
    },
    // Optional visible-character cap, display only (shows a counter and flags
    // overflow). The parent decides what to do about it; the API is the real
    // enforcement.
    maxChars: { type: Number, default: null },
  },

  emits: ["update:modelValue", "ready"],

  // Self-gate inline images off the SA-global `enable_inline_images` setting.
  // Reading it HERE (once, in the wrapper) means every rich-text control gets
  // the same behavior without each consuming site duplicating the composable
  // read + an :images-enabled binding. Fail-safe FALSE while loading / on
  // error (see the composable). An explicit imagesEnabledOverride prop still
  // wins for tests / edge cases.
  setup(props) {
    // The basic toolbar never authors images, so it skips the platform-setting
    // read entirely (no request from a surface that cannot use the answer).
    if (props.toolbar === "basic") {
      return { enableInlineImages: ref(false) };
    }
    const { enableInlineImages } = useEnableInlineImages();
    return { enableInlineImages };
  },

  data() {
    return {
      // The selectable font families (shared with both sanitizers via
      // utils/rteFonts.js -- one list, no drift).
      fontOptions: RTE_FONTS,
      // The TipTap Editor instance -- an internal detail of THIS wrapper.
      editor: null,
      // Bumped on every editor selection/transaction so the toolbar's
      // isActive()/can()-gated controls re-evaluate reactively.
      selectionTick: 0,
      // Inline-image upload state (only meaningful when imagesEnabled).
      uploading: false,
      uploadError: null,
      // Link-editor dialog state (replaces window.prompt). `open` gates the
      // overlay; `url` binds the text input; `newTab` binds the checkbox
      // (unchecked -> same tab, no target); `editing` distinguishes edit vs.
      // add for labels + clipboard-prefill suppression.
      linkDialog: {
        open: false,
        url: "",
        newTab: false,
        editing: false,
      },
    };
  },

  computed: {
    // Resolved inline-image gate: an explicit `imagesEnabledOverride` prop
    // (non-null) wins; otherwise defer to the SA-global setting read in
    // setup(). This is the single value the template + methods consult, so
    // consuming sites get correct gating for free without wiring anything.
    imagesEnabled() {
      // The basic toolbar never authors images.
      if (this.basic) return false;
      if (this.imagesEnabledOverride !== null) {
        return this.imagesEnabledOverride;
      }
      return this.enableInlineImages;
    },

    // `toolbar="basic"`: bold/italic/lists/link/line-break only.
    basic() {
      return this.toolbar === "basic";
    },

    // Visible characters (markup excluded) for the optional counter. Reads
    // modelValue, so it follows the bound HTML. Same unit the API caps.
    charCount() {
      return planDescriptionLength(this.modelValue);
    },
    overLimit() {
      return this.maxChars !== null && this.charCount > this.maxChars;
    },

    // Reflects the block the cursor is currently in so the block-format
    // dropdown shows the right label. Depends on selectionTick so it
    // recomputes as the cursor moves between paragraphs / headings.
    currentBlockFormat() {
      // eslint-disable-next-line no-unused-expressions
      this.selectionTick;
      if (!this.editor) return "paragraph";
      if (this.editor.isActive("heading", { level: 2 })) return "h2";
      if (this.editor.isActive("heading", { level: 3 })) return "h3";
      if (this.editor.isActive("heading", { level: 4 })) return "h4";
      return "paragraph";
    },

    // Reflects the font mark at the cursor so the font dropdown shows the
    // right family. "" means no font mark (the document default). Depends on
    // selectionTick so it recomputes as the cursor moves.
    currentFontFamily() {
      // eslint-disable-next-line no-unused-expressions
      this.selectionTick;
      if (!this.editor) return "";
      const attrs = this.editor.getAttributes("fontFamily");
      return attrs && attrs.font ? attrs.font : "";
    },
  },

  watch: {
    // External modelValue changes (a parent reassigning the bound HTML)
    // flow INTO the editor. Guard the well-known TipTap feedback loop:
    // only setContent when the incoming value actually differs from what
    // the editor already holds, or every keystroke's emit would bounce
    // back in and reset the cursor. `emitUpdate: false` keeps this
    // externally-sourced change from re-emitting update:modelValue.
    modelValue(newVal) {
      if (!this.editor) return;
      if (newVal === this.editor.getHTML()) return;
      this.editor.commands.setContent(newVal || "", false);
    },
    // readOnly toggles editability live (edit-mode gate on the sites).
    readOnly(locked) {
      if (this.editor) this.editor.setEditable(!locked);
    },
  },

  mounted() {
    // Captured so the keyboard-shortcuts extension's handlers reach the
    // component's dialog-open / unlink methods.
    const vm = this;
    // Cmd/Ctrl-K opens the link dialog (same flow as the toolbar button);
    // Cmd/Ctrl-Shift-K removes the link at the cursor. `Mod` maps to Cmd on
    // Mac and Ctrl elsewhere. Both return true to signal the key is handled.
    const LinkShortcuts = Extension.create({
      name: "richTextEditorLinkShortcuts",
      addKeyboardShortcuts() {
        return {
          "Mod-k": () => {
            vm.openLinkDialog();
            return true;
          },
          "Mod-Shift-k": () => {
            vm.removeLinkAtCursor();
            return true;
          },
        };
      },
    });

    // The Image NODE is registered UNCONDITIONALLY; only the image-insert UI
    // (toolbar button + file input) is gated on `imagesEnabled` in the
    // template. Two reasons: (1) `imagesEnabled` resolves from an ASYNC SA
    // setting read, so it is false at mount and may flip true afterward --
    // registering the node up front avoids rebuilding the editor when it
    // flips and guarantees the button's setImage() command exists the moment
    // the UI appears; (2) previously-stored <img> content must still render
    // in the editor view even when authoring is off. Authoring is impossible
    // without the button (there is no paste/drop insert path), and the
    // render-side sanitizer host-restricts any <img> regardless.
    const extensions = [
      LinkShortcuts,
      // StarterKit bundles paragraph, headings, bold, italic, lists,
      // blockquote, history (undo/redo), and a Link extension. We cap
      // headings at H2/H3/H4 (the render sink strips <h1>) and disable
      // its bundled Link so our explicitly-configured Link (below) is the
      // single link extension (no duplicate-extension warning).
      StarterKit.configure(
        this.basic
          ? {
              // Basic: paragraphs, bold, italic, lists, hard break, history.
              heading: false,
              blockquote: false,
              codeBlock: false,
              code: false,
              strike: false,
              underline: false,
              horizontalRule: false,
              link: false,
            }
          : {
              heading: { levels: HEADING_LEVELS },
              link: false,
            },
      ),
      // No HTMLAttributes here: target/rel are applied PER LINK from the
      // dialog (setLink) so the "open in new tab" checkbox actually
      // controls them. A blanket HTMLAttributes: { target, rel } would
      // force every link to new-tab regardless of the checkbox.
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      // Block-level <img> (matches how a welcome message / category
      // description embeds one). Base64 is NOT enabled -- images go through
      // the upload endpoint and are referenced by served URL only.
      ...(this.basic
        ? []
        : [
            Image.configure({
              inline: false,
              allowBase64: false,
            }),
          ]),
      // Text alignment on headings + paragraphs. Emits style="text-align:
      // <value>" on the block; the value is persisted through both sanitizers
      // via a value-constrained style allowlist. NO justify -- only the three
      // alignments the sanitizers permit.
      ...(this.basic
        ? []
        : [
            TextAlign.configure({
              types: TEXT_ALIGN_TYPES,
              alignments: TEXT_ALIGN_VALUES,
            }),
            // Font family as a bounded CSS class mark (see FontFamily above).
            FontFamily,
          ]),
    ];

    this.editor = new Editor({
      content: this.modelValue || "",
      editable: !this.readOnly,
      extensions,
      // The contenteditable ProseMirror root. `rte-surface` sits on the
      // wrapper, which Playwright cannot `fill`; this testid is the typing
      // target for ui-runner scenarios.
      editorProps: { attributes: { "data-testid": "rte-editable" } },
      onUpdate: ({ editor }) => {
        this.$emit("update:modelValue", editor.getHTML());
      },
      onSelectionUpdate: () => {
        this.selectionTick += 1;
      },
      onTransaction: () => {
        this.selectionTick += 1;
      },
    });

    // Parity with the Phase 1 wrapper API: signal the editor is live.
    this.$emit("ready", this.editor);
  },

  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },

  methods: {
    // Convenience: a focused command chain. Every toolbar action funnels
    // through here so focus is restored before the command runs.
    chain() {
      return this.editor.chain().focus();
    },

    // Apply the block format chosen in the dropdown. Paragraph clears any
    // heading; Heading 2/3/4 set that level. setParagraph/setHeading are
    // idempotent (unlike toggleHeading) so re-selecting the current value
    // is a no-op rather than a toggle-off.
    onBlockFormatChange(value) {
      if (value === "h2") {
        this.chain().setHeading({ level: 2 }).run();
      } else if (value === "h3") {
        this.chain().setHeading({ level: 3 }).run();
      } else if (value === "h4") {
        this.chain().setHeading({ level: 4 }).run();
      } else {
        this.chain().setParagraph().run();
      }
    },

    // Apply (or clear) the font chosen in the dropdown. "" clears the mark
    // back to the document default; any other value must be an approved
    // family (the command itself re-checks against the allowlist).
    onFontFamilyChange(value) {
      if (!value) {
        this.chain().unsetFontFamily().run();
        return;
      }
      this.chain().setFontFamily(value).run();
    },

    // Whether the current selection is aligned `value`. Reads selectionTick so
    // the button's is-active class re-evaluates as the cursor moves (mirrors
    // how bold/italic use editor.isActive in the template -- both re-run on the
    // reactive re-render selectionTick drives).
    isAlignActive(value) {
      // eslint-disable-next-line no-unused-expressions
      this.selectionTick;
      if (!this.editor) return false;
      return this.editor.isActive({ textAlign: value });
    },

    // Toolbar link button -> open the dialog (same entry point as Mod-K).
    onSetLink() {
      this.openLinkDialog();
    },

    // Open the link editor dialog, prefilling its fields:
    //  * On an existing link -> href + newTab reflect that link's attrs.
    //  * On a new link -> best-effort clipboard URL (guarded), else empty.
    // Async only so the clipboard read can be awaited; a clipboard failure
    // never blocks the dialog (it opens empty).
    async openLinkDialog() {
      if (!this.editor) return;
      const attrs = this.editor.getAttributes("link");
      const editing = !!attrs.href;
      let url = editing ? attrs.href : "";
      const newTab = editing ? attrs.target === "_blank" : false;

      // Clipboard prefill applies to NEW links only; an existing link's href
      // wins and is never overridden by the clipboard.
      if (!editing) {
        url = await this.readClipboardUrl();
      }

      this.linkDialog.url = url;
      this.linkDialog.newTab = newTab;
      this.linkDialog.editing = editing;
      this.linkDialog.open = true;

      // Autofocus the URL field once the overlay renders.
      await this.$nextTick();
      const input = this.$refs.linkUrlInput;
      if (input && typeof input.focus === "function") input.focus();
    },

    // Best-effort clipboard read for the new-link URL prefill. Guarded on
    // every axis: the API may be absent, readText may reject (permissions),
    // or the value may not be a URL. Any of those -> return "" (open empty);
    // a clipboard failure must never crash or block the dialog.
    async readClipboardUrl() {
      try {
        const clip = navigator?.clipboard;
        if (!clip || typeof clip.readText !== "function") return "";
        const text = await clip.readText();
        if (typeof text === "string" && CLIPBOARD_URL_RE.test(text.trim())) {
          return text.trim();
        }
      } catch {
        // Permission denied / unavailable / not focused -- ignore.
      }
      return "";
    },

    closeLinkDialog() {
      this.linkDialog.open = false;
    },

    // Apply the link from the dialog. Empty URL -> remove the link (mirrors
    // the old prompt's empty-string behavior). target/rel are set PER LINK
    // from the checkbox so same-tab links carry no target=_blank.
    saveLinkDialog() {
      const url = (this.linkDialog.url || "").trim();
      if (!url) {
        this.chain().extendMarkRange("link").unsetLink().run();
        this.closeLinkDialog();
        return;
      }
      const attrs = this.linkDialog.newTab && !this.basic
        ? { href: url, target: "_blank", rel: NEW_TAB_REL }
        : { href: url, target: null, rel: SAME_TAB_REL };
      this.chain().extendMarkRange("link").setLink(attrs).run();
      this.closeLinkDialog();
    },

    // Remove the link at the cursor (toolbar unlink + Mod-Shift-K). No-op
    // when the cursor is not on a link.
    removeLinkAtCursor() {
      if (!this.editor) return;
      this.chain().extendMarkRange("link").unsetLink().run();
    },

    // -- inline images (only wired when imagesEnabled) --------------------

    // Open the hidden file picker. Clear its value first so re-selecting the
    // same file still fires @change.
    openImagePicker() {
      const input = this.$refs.imageInput;
      if (input) {
        input.value = "";
        input.click();
      }
    },

    onImageInputChange(event) {
      const file = event?.target?.files?.[0];
      if (file) this.handleImageFile(file);
    },

    // Validate -> upload -> insert. Exposed on the instance so a spec can
    // drive it directly (it is the seam the button + file input funnel into).
    async handleImageFile(file) {
      this.uploadError = null;
      // Respect the imagesEnabled gate defensively even here -- callers
      // should never reach this when disabled, but never upload if they do.
      if (!this.imagesEnabled) {
        this.uploadError = "Inline images are disabled.";
        return;
      }
      const validationError = this.validateImage(file);
      if (validationError) {
        this.uploadError = validationError;
        return;
      }
      this.uploading = true;
      try {
        const uuid = await this.uploadImage(file, file.name);
        if (!uuid) {
          this.uploadError = "Upload succeeded but returned no image id.";
          return;
        }
        const src = getImageBaseUrl() + uuid;
        this.editor.chain().focus().setImage({ src, alt: file.name }).run();
        // setImage triggers onUpdate; emit defensively for tests that do not
        // rely on the async event loop.
        this.$emit("update:modelValue", this.editor.getHTML());
      } catch (e) {
        this.uploadError = this.errText(e);
      } finally {
        this.uploading = false;
      }
    },

    validateImage(file) {
      if (!file) return "Choose an image file.";
      // Checked BEFORE the MIME allowlist: a Drive / iCloud / OneDrive
      // placeholder is a supported TYPE with no bytes, and blaming the
      // format for it sends users looking for a nonexistent file
      // problem. See utils/artworkUploadContract.js.
      if (isEmptyFile(file)) return ARTWORK_UPLOAD_ERROR_EMPTY;
      if (!IMAGE_MIME_ALLOWLIST.includes(file.type)) {
        return "Unsupported file type. Choose a PNG, JPEG, GIF, WebP, or SVG.";
      }
      if (file.size > IMAGE_MAX_BYTES) {
        return "Image is too large. Maximum size is 10 MB.";
      }
      return null;
    },

    // Mirrors the productEditor _uploadArtwork / prototype path exactly:
    // multipart POST to /api/upload_image, field name `file`, Content-Type
    // unset so axios derives the multipart boundary. Returns response.data.uuid.
    async uploadImage(file, filename) {
      const formData = new FormData();
      formData.append("file", file, filename);
      const response = await api.post("/api/upload_image", formData, {
      });
      return response?.data?.uuid;
    },

    errText(e) {
      return (
        e?.response?.data?.error?.message || e?.message || "Upload failed"
      );
    },
  },
};
</script>

<style scoped>
.rte {
  display: flex;
  flex-direction: column;
}
/* Conventional WYSIWYG toolbar: one cohesive bar sitting flush on top of
   the editor surface (shares its border, no gap), buttons visually grouped
   with vertical dividers between groups. */
.rte__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
  border: 1px solid #cbd5e0;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: #f7fafc;
}
/* Toolbar dropdowns: block format (Paragraph / H2 / H3 / H4) and font
   family. Styled to sit level with the icon buttons. */
.rte__block-select,
.rte__font-select {
  height: 30px;
  padding: 0 26px 0 10px;
  border: 1px solid #cbd5e0;
  border-radius: 5px;
  background: #fff;
  font-size: 13px;
  line-height: 1;
  color: #2d3748;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232d3748' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
}
.rte__block-select:hover,
.rte__font-select:hover {
  background-color: #f7fafc;
}
.rte__block-select:focus-visible,
.rte__font-select:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}

/* Each option previews its own family so the distributor sees what they are
   picking. Native <option> font-family styling is honoured by Firefox and
   (for the closed dropdown) by the select itself; it is a progressive
   enhancement -- the label text is the real affordance. */
.rte__font-select option[value="poppins"] { font-family: 'Poppins', sans-serif; }
.rte__font-select option[value="inter"] { font-family: 'Inter', sans-serif; }
.rte__font-select option[value="lora"] { font-family: 'Lora', Georgia, serif; }
.rte__font-select option[value="playfair"] { font-family: 'Playfair Display', Georgia, serif; }
.rte__font-select option[value="oswald"] { font-family: 'Oswald', 'Arial Narrow', sans-serif; }
.rte__font-select option[value="roboto-mono"] { font-family: 'Roboto Mono', ui-monospace, monospace; }
/* Uniform square-ish icon-style buttons: borderless at rest, fill on hover,
   filled highlight when active. */
.rte__tbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  font-size: 13px;
  line-height: 1;
  color: #2d3748;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}
.rte__tbtn .material-icons {
  font-size: 20px;
  line-height: 1;
}
.rte__tbtn:hover:not(:disabled) {
  background: #e2e8f0;
  border-color: #cbd5e0;
}
.rte__tbtn:active:not(:disabled) {
  background: #cbd5e0;
}
.rte__tbtn:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
/* Pressed / active state -- e.g. the bold button when the cursor is in
   bold text. */
.rte__tbtn.is-active {
  background: #2b6cb0;
  border-color: #2b6cb0;
  color: #fff;
}
.rte__tbtn.is-active:hover:not(:disabled) {
  background: #2c5282;
  border-color: #2c5282;
}
.rte__tbtn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
/* Real divider lines between button groups. */
.rte__sep {
  width: 1px;
  align-self: stretch;
  min-height: 22px;
  background: #cbd5e0;
  margin: 4px 5px;
}
/* Uploading cue: gently spin the hourglass so the disabled image button
   reads as busy rather than broken. */
.rte__spin {
  animation: rte-spin 1.2s linear infinite;
}
@keyframes rte-spin {
  to {
    transform: rotate(360deg);
  }
}
.rte__file {
  display: none;
}
.rte__dialog-hint {
  color: #4a5568;
  font-size: 12px;
  margin: 6px 0 0;
}
.rte__count {
  color: #4a5568;
  font-size: 12px;
  margin: 4px 0 0;
  text-align: right;
}
.rte__count--over {
  color: #c53030;
  font-weight: 600;
}
.rte__error {
  color: #c53030;
  font-size: 13px;
  margin: 8px 0 0;
}
/* Inline images sit responsively within the surface. */
.rte__surface :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}
.rte__surface {
  border: 1px solid #cbd5e0;
  border-radius: 0 0 8px 8px;
  min-height: 160px;
  padding: 14px 16px;
  background: #fff;
}
/* Read-only: no toolbar, so the surface owns the full rounded border. */
.rte--readonly .rte__surface {
  border-radius: 8px;
  background: #f7fafc;
}
.rte__surface :deep(.ProseMirror) {
  outline: none;
  min-height: 130px;
}
.rte__surface :deep(.ProseMirror:focus) {
  outline: none;
}
.rte__surface :deep(blockquote) {
  border-left: 3px solid #cbd5e0;
  margin: 8px 0;
  padding-left: 12px;
  color: #4a5568;
}

/* Link editor dialog: a centered modal panel over a dimming overlay. Plain
   Vue + scoped CSS -- no dialog dependency, matching the neutral slate
   palette used elsewhere. */
.rte__dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 32, 44, 0.45);
  padding: 20px;
}
.rte__dialog {
  width: 100%;
  max-width: 380px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  padding: 20px 22px;
}
.rte__dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 14px;
}
.rte__dialog-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  margin: 0 0 6px;
}
.rte__dialog-input {
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  color: #1a202c;
}
.rte__dialog-input:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
.rte__dialog-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #2d3748;
  margin: 14px 0 0;
  cursor: pointer;
}
.rte__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
.rte__dialog-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  cursor: pointer;
}
.rte__dialog-btn:hover {
  background: #f7fafc;
}
.rte__dialog-btn:focus-visible {
  outline: 2px solid #2b6cb0;
  outline-offset: 1px;
}
.rte__dialog-btn--primary {
  background: #2b6cb0;
  border-color: #2b6cb0;
  color: #fff;
}
.rte__dialog-btn--primary:hover {
  background: #2c5282;
  border-color: #2c5282;
}
</style>
